<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  team: { type: Object, required: true },
  members: { type: Array, default: () => [] },
  teammates: { type: Array, default: () => [] },
  isUpdating: { type: Boolean, default: false },
});

const emit = defineEmits(['back', 'addMember', 'removeMember']);

const { t } = useI18n();

const isMenuOpen = ref(false);

const memberIds = computed(() => props.members.map(member => member.id));

const candidates = computed(() =>
  props.teammates.filter(teammate => !memberIds.value.includes(teammate.id))
);

const addMember = agentId => {
  isMenuOpen.value = false;
  emit('addMember', agentId);
};
</script>

<template>
  <div class="flex flex-col w-full gap-5 mx-auto max-w-5xl">
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-chevron-left"
      :label="t('TEAM_PAGE.TEAM_DETAIL.BACK')"
      class="self-start"
      @click="emit('back')"
    />

    <header class="flex flex-wrap items-center gap-3">
      <div class="min-w-0">
        <h1 class="m-0 text-xl font-semibold tracking-tight text-n-slate-12">
          {{ team.name }}
        </h1>
        <p class="mt-1 mb-0 text-sm text-n-slate-11">
          {{ team.description || t('TEAM_PAGE.TEAMS.NO_DESCRIPTION') }}
        </p>
      </div>

      <OnClickOutside class="relative ms-auto" @trigger="isMenuOpen = false">
        <Button
          variant="outline"
          color="slate"
          icon="i-lucide-plus"
          :label="t('TEAM_PAGE.TEAM_DETAIL.ADD_MEMBER')"
          :disabled="isUpdating"
          :aria-expanded="isMenuOpen"
          @click="isMenuOpen = !isMenuOpen"
        />
        <div
          v-if="isMenuOpen"
          role="menu"
          :aria-label="t('TEAM_PAGE.TEAM_DETAIL.ADD_MEMBER_LABEL')"
          class="absolute z-20 w-56 py-1 overflow-hidden shadow-lg end-0 top-11 rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container"
        >
          <p
            v-if="!candidates.length"
            class="px-3 py-2 m-0 text-sm text-n-slate-10"
          >
            {{ t('TEAM_PAGE.TEAM_DETAIL.ALL_ADDED') }}
          </p>
          <button
            v-for="teammate in candidates"
            :key="teammate.id"
            type="button"
            role="menuitem"
            class="flex items-center w-full gap-2.5 px-3 py-2 text-sm text-start text-n-slate-12 hover:bg-n-alpha-2"
            @click="addMember(teammate.id)"
          >
            <Avatar
              :name="teammate.name"
              :src="teammate.thumbnail"
              :size="24"
              rounded-full
            />
            <span class="flex-1 min-w-0 truncate">{{ teammate.name }}</span>
          </button>
        </div>
      </OnClickOutside>
    </header>

    <div
      v-if="!members.length"
      class="flex items-center justify-center py-16 rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2"
    >
      <span class="text-sm text-n-slate-11">
        {{ t('TEAM_PAGE.TEAM_DETAIL.EMPTY') }}
      </span>
    </div>
    <div
      v-else
      class="overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2"
    >
      <ul class="m-0 list-none divide-y divide-n-weak">
        <li
          v-for="member in members"
          :key="member.id"
          class="flex items-center gap-3 px-4 py-3.5"
        >
          <Avatar
            :name="member.name"
            :src="member.thumbnail"
            :size="32"
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <p class="m-0 text-sm font-medium truncate text-n-slate-12">
              {{ member.name }}
            </p>
            <p class="m-0 text-xs truncate text-n-slate-10">
              {{ member.email }}
            </p>
          </div>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            :disabled="isUpdating"
            :aria-label="
              t('TEAM_PAGE.TEAM_DETAIL.REMOVE_MEMBER', {
                name: member.name,
                team: team.name,
              })
            "
            @click="emit('removeMember', member.id)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>
